import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { LINKS } from "@/lib/site";
import "./FitCalculator.css";

/** Lowest monthly scale investment used for the fit filter. */
const ENTRY_INVESTMENT = 27000;

/** Scale tiers. The build is the same figure, paid once. */
const tiers = [
  { name: "The Pod", price: 27000 },
  { name: "The Team", price: 33000 },
  { name: "The Enterprise", price: 45000 },
];

const acvBands = [
  { id: "u5", label: "Under $5k", value: 3500 },
  { id: "5-15", label: "$5-15k", value: 10000 },
  { id: "15-40", label: "$15-40k", value: 27500 },
  { id: "40-100", label: "$40-100k", value: 70000 },
  { id: "100p", label: "$100k+", value: 150000 },
];

const dealBands = [
  { id: "1", label: "1", value: 1 },
  { id: "2", label: "2", value: 2 },
  { id: "3-4", label: "3-4", value: 3.5 },
  { id: "5-8", label: "5-8", value: 6.5 },
  { id: "9p", label: "9+", value: 10 },
];

const formatMoney = (n) => {
  if (n >= 1000) {
    const k = n / 1000;
    const rounded = Number.isInteger(k) ? k.toFixed(0) : k.toFixed(1).replace(/\.0$/, "");
    return `$${rounded}k`;
  }
  return `$${Math.round(n).toLocaleString()}`;
};

/** Largest tier the monthly closed revenue still comfortably returns. */
const tierFor = (revenue) =>
  [...tiers].reverse().find((t) => revenue >= t.price * 3) ?? tiers[0];

const verdictFor = (coverage) => {
  if (coverage < 1) {
    return {
      id: "no",
      title: "Below the monthly fee",
      body: "Those deals would be worth less than the monthly fee alone.",
      panel: "bg-[#F3E4E0] border-ink text-ink",
      accent: "text-[#991B1B]",
      bar: "bg-[#DC2626]",
      cta: null,
    };
  }
  if (coverage < 3) {
    return {
      id: "tight",
      title: "Check your margins",
      body: "Deal value exceeds the fee. Check your margins, payment terms and commission before scaling.",
      panel: "bg-paper border-ink text-ink",
      accent: "text-flame",
      bar: "bg-flame",
      cta: { href: "#pricing", label: "See pricing", external: false },
    };
  }
  return {
    id: "fit",
    title: "Worth a conversation",
    body: "That level of revenue supports a sales team. We’ll agree the right structure around your margins, payment terms and commission.",
    panel: "bg-ink border-ink text-paper",
    accent: "text-cobalt stat-on-ink",
    bar: "bg-cobalt",
    cta: { href: LINKS.cal, label: "Book a meeting", external: true },
  };
};

const BandRow = ({ label, options, value, onChange, testId }) => (
  <div data-testid={testId}>
    <p className="font-mono text-sm font-semibold uppercase tracking-[0.14em] text-ink mb-3 text-pretty">{label}</p>
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.id)}
            className={`px-3.5 py-2.5 font-mono text-sm md:text-base font-semibold uppercase tracking-[0.08em] border transition-colors duration-150 ${
              active
                ? "bg-ink text-paper border-ink"
                : "bg-paper text-ink border-ink/25 hover:border-ink"
            }`}
            data-testid={`${testId}-${o.id}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const ProposalRow = ({ label, value, note, emphasis }) => (
  <div className="fit-proposal-row flex items-baseline justify-between gap-4 py-3">
    <div className="min-w-0">
      <p className="font-mono text-base font-semibold uppercase tracking-[0.08em] text-pretty">{label}</p>
      {note ? <p className="mt-1 text-base font-light leading-snug text-pretty">{note}</p> : null}
    </div>
    <p
      className={`shrink-0 font-display font-semibold tabular-nums tracking-tighter leading-none ${
        emphasis ? "text-3xl md:text-4xl" : "text-2xl md:text-3xl"
      }`}
    >
      {value}
    </p>
  </div>
);

export const FitCalculator = () => {
  const [acvId, setAcvId] = useState("15-40");
  const [dealsId, setDealsId] = useState("3-4");

  const acv = acvBands.find((b) => b.id === acvId) ?? acvBands[2];
  const deals = dealBands.find((b) => b.id === dealsId) ?? dealBands[2];

  const { revenue, verdict, tier } = useMemo(() => {
    const revenue = acv.value * deals.value;
    const coverage = revenue / ENTRY_INVESTMENT;
    const tier = tierFor(revenue);
    return {
      revenue,
      verdict: verdictFor(coverage),
      tier,
    };
  }, [acv, deals]);

  const showProposal = verdict.id !== "no";

  return (
    <div className="mt-10 md:mt-14" data-testid="fit-calculator">
      <p className="font-mono text-sm font-semibold uppercase tracking-[0.18em] text-ink mb-4 text-pretty">Fit check</p>
      <h3 className="font-display font-semibold tracking-[-0.02em] text-3xl md:text-4xl lg:text-5xl leading-[0.95] text-ink max-w-3xl text-balance">
        We&apos;re not for <span className="font-editorial italic font-medium text-cobalt">everyone.</span>
      </h3>
      <p className="mt-4 text-lg md:text-xl font-light text-ink leading-relaxed max-w-2xl text-pretty">
        Best fit: B2B companies at $1M+/year with a proven offer and enough deal value to support a sales team.
      </p>

      <div className="mt-8 border border-ink bg-paper overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-6 p-6 md:p-9 lg:p-10 space-y-8 border-b lg:border-b-0 lg:border-r border-ink">
            <BandRow
              label="Average deal size"
              options={acvBands}
              value={acvId}
              onChange={setAcvId}
              testId="fit-acv"
            />
            <BandRow
              label="New customers per month"
              options={dealBands}
              value={dealsId}
              onChange={setDealsId}
              testId="fit-deals"
            />

            {showProposal ? (
              <div className="pt-2" data-testid="fit-summary">
                <p className="font-mono text-sm font-semibold uppercase tracking-[0.14em] text-ink mb-2 text-pretty">
                  Value of new deals per month
                </p>
                <p
                  className="font-display font-semibold text-4xl md:text-5xl tracking-tighter tabular-nums leading-none text-ink text-pretty"
                  data-testid="fit-revenue"
                >
                  {formatMoney(revenue)}
                </p>
                <p className="mt-3 text-base font-light leading-snug text-ink text-pretty">
                  Estimate from your selected ranges. This is deal value, not cash collected.
                </p>
              </div>
            ) : (
              <div className="pt-2" data-testid="fit-summary">
                <p className="font-mono text-sm font-semibold uppercase tracking-[0.14em] text-ink mb-2 text-pretty">
                  Value of new deals per month
                </p>
                <p
                  className="font-display font-semibold text-4xl md:text-5xl tracking-tighter tabular-nums leading-none text-ink text-pretty"
                  data-testid="fit-revenue"
                >
                  {formatMoney(revenue)}
                </p>
              </div>
            )}
          </div>

          <div
            className={`fit-verdict-panel lg:col-span-6 p-6 md:p-9 lg:p-10 flex flex-col border-t lg:border-t-0 ${verdict.panel}`}
            data-testid={`fit-verdict-${verdict.id}`}
          >
            <div className={`h-1.5 w-24 mb-6 ${verdict.bar}`} aria-hidden />
            <p className="font-mono text-sm font-semibold uppercase tracking-[0.16em] text-pretty">Result</p>

            <div aria-live="polite" aria-atomic="true">
              <AnimatePresence mode="wait">
                <motion.div
                  key={verdict.id + acvId + dealsId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h4
                    className={`fit-verdict-title mt-3 font-display font-semibold tracking-[-0.03em] leading-[1.05] ${verdict.accent}`}
                  >
                    {verdict.title}
                  </h4>
                  <p className="mt-4 text-lg md:text-xl font-light leading-snug max-w-md text-pretty">{verdict.body}</p>
                </motion.div>
              </AnimatePresence>
            </div>

            {showProposal ? (
              <div className="mt-8 pt-2 border-t border-current/20 divide-y divide-current/15" data-testid="fit-proposal">
                <ProposalRow
                  label={`${tier.name} per month`}
                  note="Plus commission."
                  value={formatMoney(tier.price)}
                />
              </div>
            ) : null}

            <div className="mt-auto pt-8">
              {verdict.cta ? (
                <a
                  href={verdict.cta.href}
                  {...(verdict.cta.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className={`group inline-flex items-center gap-2 px-6 py-4 font-mono text-sm font-semibold uppercase tracking-[0.12em] border transition-all duration-200 ${
                    verdict.id === "fit"
                      ? "bg-cobalt text-white border-paper/20 hover:-translate-y-0.5 hover:shadow-hard"
                      : "bg-ink text-paper border-ink hover:bg-cobalt"
                  }`}
                  data-testid="fit-cta"
                >
                  {verdict.cta.label}
                  <ArrowUpRight
                    size={16}
                    strokeWidth={2.5}
                    className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                  />
                </a>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
